import { useState, useEffect } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import { API, formatCurrency } from './constants';

export default function CatalogImportTab({ categories, onImportComplete }) {
  const [importData, setImportData] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const products = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const product = {};
      
      headers.forEach((header, idx) => {
        const value = values[idx] || '';
        
        if (header === 'nombre' || header === 'name') {
          product.name = value;
        } else if (header === 'precio' || header === 'price') {
          product.price = parseFloat(value) || 0;
        } else if (header === 'costo' || header === 'cost') {
          product.cost = parseFloat(value) || 0;
        } else if (header === 'descripcion' || header === 'description') {
          product.description = value;
        } else if (header === 'categoria' || header === 'category' || header === 'category_id') {
          product.category_id = value;
        } else if (header === 'imagen' || header === 'image_url') {
          product.image_url = value;
        } else if (header === 'stock') {
          product.stock = parseInt(value) || 100;
        }
      });
      
      if (product.name) {
        products.push(product);
      }
    }
    
    return products;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setParseError(null);
    setImportResult(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        let products = [];
        
        if (file.name.endsWith('.json')) {
          const json = JSON.parse(text);
          products = Array.isArray(json) ? json : json.products || [];
        } else {
          products = parseCSV(text);
        }
        
        if (products.length === 0) {
          setParseError('No se encontraron productos en el archivo');
          return;
        }
        
        setImportData(products);
        toast.success(`${products.length} productos listos para importar`);
      } catch (err) {
        setParseError(`Error al parsear archivo: ${err.message}`);
        toast.error('Error al leer el archivo');
      }
    };
    
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importData?.length) return;
    
    setImporting(true);
    try {
      const response = await fetch(`${API}/admin/catalog/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ products: importData, mode: 'update' })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setImportResult(result);
        toast.success(`Importados: ${result.imported}, Actualizados: ${result.updated}`);
        onImportComplete?.();
      } else {
        toast.error(result.detail || 'Error en importación');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportData(null);
    setParseError(null);
    setImportResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Importar Catálogo</h2>
          <p className="text-sm text-gray-500">Actualiza productos desde archivo CSV o JSON</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Ver Formato
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Formato del Archivo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">CSV (separado por comas)</h4>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`nombre,precio,costo,descripcion,categoria,imagen,stock
Café Americano,35,12,Café recién hecho,cat_bebidas,https://...,100
Sandwich Pollo,95,40,Delicioso sandwich,cat_comida_fria,https://...,50`}
                </pre>
              </div>
              <div>
                <h4 className="font-medium mb-2">JSON</h4>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`{
  "products": [
    {
      "name": "Café Americano",
      "price": 35,
      "cost": 12,
      "category_id": "cat_bebidas"
    }
  ]
}`}
                </pre>
              </div>
              <p className="text-gray-500">
                Columnas requeridas: <strong>nombre</strong> (o name), <strong>precio</strong> (o price)
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Upload Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-2">Sube tu archivo</h3>
          <p className="text-sm text-gray-500 mb-4">CSV o JSON con datos de productos</p>
          
          <input
            type="file"
            accept=".csv,.json"
            onChange={handleFileUpload}
            className="hidden"
            id="catalog-file"
          />
          <label htmlFor="catalog-file">
            <Button asChild variant="outline">
              <span>Seleccionar Archivo</span>
            </Button>
          </label>
        </div>
        
        {parseError && (
          <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span>{parseError}</span>
          </div>
        )}
      </div>

      {/* Preview */}
      {importData && !importResult && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-900">
              Vista Previa ({importData.length} productos)
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={resetImport}>
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
              <Button 
                size="sm" 
                onClick={handleImport} 
                disabled={importing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {importing ? 'Importando...' : 'Confirmar Importación'}
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Nombre</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Precio</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Costo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Categoría</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {importData.slice(0, 20).map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{product.name}</td>
                    <td className="px-4 py-2 text-sm">{formatCurrency(product.price)}</td>
                    <td className="px-4 py-2 text-sm">{formatCurrency(product.cost)}</td>
                    <td className="px-4 py-2 text-sm">
                      <Badge variant="outline">{product.category_id || 'Sin categoría'}</Badge>
                    </td>
                    <td className="px-4 py-2 text-sm">{product.stock || 100}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {importData.length > 20 && (
              <p className="text-center text-sm text-gray-500 py-2">
                ...y {importData.length - 20} productos más
              </p>
            )}
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Importación Completada</h3>
              <p className="text-sm text-gray-500">
                {importResult.imported} nuevos, {importResult.updated} actualizados
              </p>
            </div>
          </div>
          
          {importResult.errors?.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-700 mb-2">Errores:</h4>
              <ul className="text-sm text-red-600 space-y-1">
                {importResult.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          
          <Button className="mt-4" onClick={resetImport}>
            Importar Otro Archivo
          </Button>
        </div>
      )}

      {/* Quick Tips */}
      <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
        <h3 className="font-semibold text-emerald-800 mb-3">Consejos</h3>
        <ul className="text-sm text-emerald-700 space-y-2">
          <li>• Los productos existentes se actualizarán por nombre</li>
          <li>• Los productos nuevos se crearán automáticamente</li>
          <li>• Usa los IDs de categoría existentes: {categories.map(c => c.category_id).join(', ')}</li>
          <li>• Exporta primero el catálogo actual como referencia</li>
        </ul>
      </div>
    </div>
  );
}
